/**
 * Zentrale Query-Key Factory für React Query
 * 
 * Verhindert inkonsistente Keys und ermöglicht saubere Invalidierung.
 * Alle Query Keys müssen über diese Factory erstellt werden!
 * 
 * @module client/src/lib/queryKeys
 */

export const queryKeys = {
  /**
   * Restaurant-bezogene Query Keys
   */
  restaurants: {
    // Alle Restaurants (base key für Invalidierung)
    all: ['restaurants'] as const,
    
    // Restaurant-Listen mit Filtern
    lists: () => [...queryKeys.restaurants.all, 'list'] as const,
    list: (filters: Record<string, any>) => 
      [...queryKeys.restaurants.lists(), filters] as const,
    
    // Restaurant-Suche (inkl. alle Filter)
    searches: () => [...queryKeys.restaurants.all, 'search'] as const,
    search: (params: Record<string, any>) => 
      [...queryKeys.restaurants.searches(), params] as const,
    searchKey: (...parts: Array<string | number | boolean>) =>
      [...queryKeys.restaurants.searches(), ...parts] as const,
    
    // Einzelne Restaurants
    details: () => [...queryKeys.restaurants.all, 'detail'] as const,
    detail: (id: string | number) => 
      [...queryKeys.restaurants.details(), String(id)] as const,
    
    // Restaurant by Slug
    bySlugs: () => [...queryKeys.restaurants.all, 'by-slug'] as const,
    bySlug: (slug: string) => 
      [...queryKeys.restaurants.bySlugs(), slug] as const,
    
    // Restaurant-spezifische Sub-Ressourcen
    openingHours: (id: string | number) => 
      [...queryKeys.restaurants.detail(id), 'opening-hours'] as const,
    deliveryHours: (id: string | number) => 
      [...queryKeys.restaurants.detail(id), 'delivery-hours'] as const,
    images: (id: string | number) => 
      [...queryKeys.restaurants.detail(id), 'images'] as const,
    menus: (id: string | number) => 
      [...queryKeys.restaurants.detail(id), 'menus'] as const,
    insights: (id: string | number) => 
      [...queryKeys.restaurants.detail(id), 'insights'] as const,
  },
  
  /**
   * Kategorien
   */
  categories: {
    all: ['categories'] as const,
    list: () => [...queryKeys.categories.all, 'list'] as const,
    detail: (id: string | number) => 
      [...queryKeys.categories.all, String(id)] as const,
  },
  
  /**
   * Küchen/Cuisines
   */
  cuisines: {
    all: ['cuisines'] as const,
    list: () => [...queryKeys.cuisines.all, 'list'] as const,
  },
  
  /**
   * Skills
   */
  skills: {
    all: ['skills'] as const,
    list: () => [...queryKeys.skills.all, 'list'] as const,
  },
  
  /**
   * Admin-Bereich
   */
  admin: {
    all: ['admin'] as const,
    insights: () => [...queryKeys.admin.all, 'insights'] as const,
    globalInsights: () => [...queryKeys.admin.insights(), 'global'] as const,
    metrics: () => [...queryKeys.admin.all, 'metrics'] as const,
    logs: (filters?: Record<string, any>) => 
      [...queryKeys.admin.all, 'logs', filters || {}] as const,
  },
  
  /**
   * User/Auth
   */
  user: {
    all: ['user'] as const,
    current: () => [...queryKeys.user.all, 'current'] as const,
    favorites: () => [...queryKeys.user.all, 'favorites'] as const,
  },
  
  /**
   * Config
   */
  config: {
    all: ['config'] as const,
    app: () => [...queryKeys.config.all, 'app'] as const,
  },
};

/**
 * Helper: Invalidiert alle Restaurant-Listen und Suchen
 * Nutze diese Funktion nach Restaurant-Mutations!
 */
export function invalidateAllRestaurantLists(queryClient: any) {
  // Invalidiere alle Listen
  queryClient.invalidateQueries({ 
    queryKey: queryKeys.restaurants.lists(),
    refetchType: 'all'
  });
  
  // Invalidiere alle Suchen
  queryClient.invalidateQueries({ 
    queryKey: queryKeys.restaurants.searches(),
    refetchType: 'all'
  });
}

/**
 * Helper: Invalidiert Restaurant Detail + alle Sub-Ressourcen
 */
export function invalidateRestaurantDetail(
  queryClient: any, 
  id: string | number,
  options?: { includeSubResources?: boolean }
) {
  // Detail (structured key)
  queryClient.invalidateQueries({ 
    queryKey: queryKeys.restaurants.detail(id),
    refetchType: 'all'
  });
  
  // Also invalidate URL-based key (used by RestaurantEditPage)
  queryClient.invalidateQueries({ 
    queryKey: [`/api/restaurants/${id}`],
    refetchType: 'all'
  });
  
  // Sub-Ressourcen (optional)
  if (options?.includeSubResources !== false) {
    queryClient.invalidateQueries({ 
      queryKey: queryKeys.restaurants.openingHours(id)
    });
    queryClient.invalidateQueries({ 
      queryKey: queryKeys.restaurants.deliveryHours(id)
    });
    queryClient.invalidateQueries({ 
      queryKey: queryKeys.restaurants.images(id)
    });
    queryClient.invalidateQueries({ 
      queryKey: queryKeys.restaurants.menus(id)
    });
    queryClient.invalidateQueries({ 
      queryKey: queryKeys.restaurants.insights(id)
    });
  }
}

/**
 * Helper: Invalidiert Restaurant by Slug
 */
export function invalidateRestaurantBySlug(
  queryClient: any,
  slug: string
) {
  queryClient.invalidateQueries({ 
    queryKey: queryKeys.restaurants.bySlug(slug),
    refetchType: 'all'
  });
}

/**
 * Helper: Complete Invalidation nach Restaurant-Mutation
 * Nutze diese nach create/update/delete!
 */
export function invalidateAfterRestaurantMutation(
  queryClient: any,
  restaurant: { id: string | number; slug?: string },
  oldRestaurant?: { slug?: string }
) {
  // Detail + Sub-Ressourcen
  invalidateRestaurantDetail(queryClient, restaurant.id, { 
    includeSubResources: true 
  });
  
  // Slugs (alt + neu)
  if (restaurant.slug) {
    invalidateRestaurantBySlug(queryClient, restaurant.slug);
  }
  if (oldRestaurant?.slug && oldRestaurant.slug !== restaurant.slug) {
    invalidateRestaurantBySlug(queryClient, oldRestaurant.slug);
  }
  
  // Alle Listen + Suchen
  invalidateAllRestaurantLists(queryClient);
  
  // Admin Insights
  queryClient.invalidateQueries({ 
    queryKey: queryKeys.admin.globalInsights(),
    refetchType: 'all'
  });
}
